import type { z } from 'zod';
import type { EfemPeripheralMatrixResponseSchema } from '@cnapse/shared/schemas.js';
import type { ContainerSlotScanActiveJob, BatchWaferTransferActiveJob } from '@cnapse/shared/schemas.js';
import type {
  BATCH_WAFER_TRANSFER_JOB_PHASE,
  CONTAINER_SLOT_SCAN_JOB_PHASE,
} from '@cnapse/shared/data.types.js';
import { waferImagingTemplatesService } from '../services/WaferImagingTemplatesService';
import { jobsService } from '../services/JobsService';
import { efemService } from '../services/EfemService';
import type { WaferImagingTemplateListItem } from './waferImagingTemplateFilters';
import {
  clearEfemSlotMapCache,
  invalidateEfemSlotMapsForGateway,
} from './efemSlotMapCache';

/** Reuse across wizard step navigation within one session. */
const DEFAULT_TTL_MS = 120_000;
/** Active-job guard can be slightly stale while navigating steps. */
const ACTIVE_JOBS_TTL_MS = 20_000;

type EfemPeripheralMatrixData = z.infer<typeof EfemPeripheralMatrixResponseSchema>['data'];
type EfemClientsData = Awaited<ReturnType<typeof efemService.getEfemClients>>;
type LoadPortsData = Awaited<ReturnType<typeof efemService.getEfemLoadPorts>>;

export interface ActiveJobsSnapshot {
  scans: ContainerSlotScanActiveJob[];
  transfers: BatchWaferTransferActiveJob[];
}

interface CacheEntry<T> {
  data: T;
  fetchedAt: number;
}

function isFresh<T>(entry: CacheEntry<T> | null | undefined, ttlMs: number): entry is CacheEntry<T> {
  return entry != null && Date.now() - entry.fetchedAt <= ttlMs;
}

let templatesCache: CacheEntry<WaferImagingTemplateListItem[]> | null = null;
let clientsCache: CacheEntry<EfemClientsData> | null = null;
let activeJobsCache: CacheEntry<ActiveJobsSnapshot> | null = null;

const matrixCache = new Map<string, CacheEntry<EfemPeripheralMatrixData>>();
const loadPortsCache = new Map<string, CacheEntry<LoadPortsData>>();

export function peekWaferImagingTemplates(
  ttlMs: number = DEFAULT_TTL_MS,
): WaferImagingTemplateListItem[] | null {
  return isFresh(templatesCache, ttlMs) ? templatesCache.data : null;
}

export async function listWaferImagingTemplatesCached(options?: {
  force?: boolean;
  ttlMs?: number;
}): Promise<WaferImagingTemplateListItem[]> {
  const ttlMs = options?.ttlMs ?? DEFAULT_TTL_MS;
  if (!options?.force && isFresh(templatesCache, ttlMs)) {
    return templatesCache.data;
  }
  const list = await waferImagingTemplatesService.listWaferImagingTemplates();
  templatesCache = { data: list, fetchedAt: Date.now() };
  return list;
}

export function peekEfemPeripheralMatrix(
  gatewaySerialNumber: string,
  ttlMs: number = DEFAULT_TTL_MS,
): EfemPeripheralMatrixData | null {
  const entry = matrixCache.get(gatewaySerialNumber);
  return isFresh(entry, ttlMs) ? entry.data : null;
}

export async function getEfemPeripheralMatrixCached(
  gatewaySerialNumber: string,
  options?: { force?: boolean; ttlMs?: number },
): Promise<EfemPeripheralMatrixData> {
  const ttlMs = options?.ttlMs ?? DEFAULT_TTL_MS;
  const entry = matrixCache.get(gatewaySerialNumber);
  if (!options?.force && isFresh(entry, ttlMs)) {
    return entry.data;
  }
  const data = await efemService.getEfemPeripheralMatrix(gatewaySerialNumber);
  matrixCache.set(gatewaySerialNumber, { data, fetchedAt: Date.now() });
  return data;
}

export function peekEfemLoadPorts(
  gatewaySerialNumber: string,
  ttlMs: number = DEFAULT_TTL_MS,
): LoadPortsData | null {
  const entry = loadPortsCache.get(gatewaySerialNumber);
  return isFresh(entry, ttlMs) ? entry.data : null;
}

export async function getEfemLoadPortsCached(
  gatewaySerialNumber: string,
  options?: { force?: boolean; ttlMs?: number },
): Promise<LoadPortsData> {
  const ttlMs = options?.ttlMs ?? DEFAULT_TTL_MS;
  const entry = loadPortsCache.get(gatewaySerialNumber);
  if (!options?.force && isFresh(entry, ttlMs)) {
    return entry.data;
  }
  const data = await efemService.getEfemLoadPorts(gatewaySerialNumber);
  loadPortsCache.set(gatewaySerialNumber, { data, fetchedAt: Date.now() });
  return data;
}

export function peekEfemClients(ttlMs: number = DEFAULT_TTL_MS): EfemClientsData | null {
  return isFresh(clientsCache, ttlMs) ? clientsCache.data : null;
}

export async function getEfemClientsCached(options?: {
  force?: boolean;
  ttlMs?: number;
}): Promise<EfemClientsData> {
  const ttlMs = options?.ttlMs ?? DEFAULT_TTL_MS;
  if (!options?.force && isFresh(clientsCache, ttlMs)) {
    return clientsCache.data;
  }
  const data = await efemService.getEfemClients();
  clientsCache = { data, fetchedAt: Date.now() };
  return data;
}

export async function getActiveJobsSnapshotCached(options?: {
  force?: boolean;
  ttlMs?: number;
}): Promise<ActiveJobsSnapshot> {
  const ttlMs = options?.ttlMs ?? ACTIVE_JOBS_TTL_MS;
  if (!options?.force && isFresh(activeJobsCache, ttlMs)) {
    return activeJobsCache.data;
  }
  const { items } = await jobsService.listActiveJobs({ view: 'mine', type: 'all', limit: 50 });
  const scans: ContainerSlotScanActiveJob[] = items
    .filter((job) => job.jobType === 'container_slot_scan')
    .map((job) => ({
      jobId: job.jobId,
      phase: job.phase as CONTAINER_SLOT_SCAN_JOB_PHASE,
      gatewaySerialNumber: job.gatewaySerialNumber ?? null,
      efemClientName: job.efemName ?? null,
      loadPortId: job.loadPortId ?? 0,
      containerId: job.containerId ?? null,
      slotNumbers: [],
      totalSlots: job.totalItems,
      completedSlots: job.completedItems,
      phaseError: null,
      initiatedByUserId: job.initiatedByUserId ?? null,
      startedAt: job.startedAt ?? undefined,
      finishedAt: job.finishedAt ?? null,
      updatedAt: job.updatedAt,
    }));
  const transfers: BatchWaferTransferActiveJob[] = items
    .filter((job) => job.jobType === 'batch_wafer_transfer')
    .map((job) => ({
      jobId: job.jobId,
      phase: job.phase as BATCH_WAFER_TRANSFER_JOB_PHASE,
      efemClientId: job.efemClientId,
      totalTransfers: job.totalItems,
      completedTransfers: job.completedItems,
      phaseError: null,
      initiatedByUserId: job.initiatedByUserId ?? 0,
      startedAt: job.startedAt ?? undefined,
      finishedAt: job.finishedAt ?? null,
      updatedAt: job.updatedAt,
    }));
  const data: ActiveJobsSnapshot = { scans, transfers };
  activeJobsCache = { data, fetchedAt: Date.now() };
  return data;
}

/** After clamp/unload/bind — refresh gateway-scoped EFEM data. */
export function invalidateWizardGatewayCache(gatewaySerialNumber: string): void {
  invalidateEfemSlotMapsForGateway(gatewaySerialNumber);
  loadPortsCache.delete(gatewaySerialNumber);
  matrixCache.delete(gatewaySerialNumber);
  activeJobsCache = null;
}

export function invalidateWaferImagingTemplatesCache(): void {
  templatesCache = null;
}

export function clearJobWizardDataCaches(): void {
  templatesCache = null;
  clientsCache = null;
  activeJobsCache = null;
  matrixCache.clear();
  loadPortsCache.clear();
  clearEfemSlotMapCache();
}
